import { type IScope } from 'angular';
import { adapter, Directive } from '../index';
import { A9DecoratorsCommon } from './A9DecoratorsCommon';

// docs source: https://albatros-golf.atlassian.net/wiki/spaces/AFED/pages/121110543/A9+Component
export interface IA9Component<Scope extends IScope = IScope, TElement extends JQLite = JQLite> {
    /**
     * component element
     */
    selector: string;
    /**
     * services to be used in component directive functions (e.g. in link)
     */
    providers?: string[];
    /**
     * component directive scope
     *
     * source: https://stackoverflow.com/questions/14050195/what-is-the-difference-between-and-in-directive-scope-in-angularjs
     *
     * '@' binding is for passing strings. These strings support {{}} expressions for interpolated values.
     * For example: . The interpolated expression is evaluated against directive's parent scope.
     *
     * '=' binding is for two-way model binding. The model in parent scope is linked to the model
     * in the directive's isolated scope. Changes to one model affects the other, and vice versa.
     *
     * '&' binding is for passing a method into your directive's scope so that it can be called within
     * your directive. The method is pre-bound to the directive's parent scope, and supports arguments.
     * For example if the method is hello(name) in parent scope, then in order to execute the method from
     * inside your directive, you must call $scope.hello({name:'world'})
     *
     * '@' Attribute string binding
     *
     * '=' Two-way model binding
     *
     * '&' Callback method binding
     *
     **/

    scope?: angular.IDirective<Scope, TElement>['scope'];
    /**
     * replace component directives
     */
    replace?: angular.IDirective<Scope, TElement>['replace'];
    /**
     * restrict component directives
     * src: https://stackoverflow.com/a/29841758
     * 'A' - only matches attribute name
     * 'E' - only matches element name
     * 'C' - only matches class name
     * 'M' - only matches comment
     */
    restrict?: angular.IDirective<Scope, TElement>['restrict'];
    terminal?: angular.IDirective<Scope, TElement>['terminal'];
    /**
     * component directive priority
     */
    priority?: angular.IDirective<Scope, TElement>['priority'];
    /**
     * whether to bind scope to controller
     */
    bindToController?: boolean | { [boundProperty: string]: string } | undefined;
    /**
     * Placeholder for controller in the template.
     */
    controllerAs?: angular.IDirective<Scope, TElement>['controllerAs'];
    /**
     * component transclude
     */
    transclude?: angular.IDirective<Scope, TElement>['transclude'];
    require?: angular.IDirective<Scope, TElement>['require'];
    compile?($element: any, $attrs: angular.IAttributes): any;
    /**
     * component's link
     * @param $scope
     * @param $element
     * @param $attrs
     * @param $ctrl
     * @param $transclude
     */
    link?($scope: any, $element: any, $attrs: angular.IAttributes, $ctrl: angular.IController, $transclude: angular.ITranscludeFunction): void;
    /**
     * component template function
     * @param element
     * @param attrs
     */
    template?: angular.IDirective<Scope, TElement>['template'];
}

function drMaxRepeatExpressionCompile<Scope extends IScope, TElement extends JQLite>(template: IA9Component<Scope, TElement>['template']): any {
    if (typeof template === 'string' && template.indexOf('<drmax-repeat') !== -1) {
        template = String(template).replace(
            /<drmax-repeat\s+(\d+)\s+(\d+)>\s+(.*?)\s+<\/drmax-repeat>/gs,
            function (_$0, start, end, templateTemplate) {
                let ret = '';
                for (let i = start; i <= end; i++) {
                    ret += templateTemplate.replace(/_step_/g, i);
                }
                return ret;
            }
        );
    }
    return template;
}

function decorateLite<Scope extends IScope, TElement extends JQLite>(object: IA9Component<Scope, TElement>, controller: any) {
    const injectorParams = A9DecoratorsCommon.parseConstructorParams(controller);

    Object.defineProperty(controller, '$inject', {
        get: function () {
            return injectorParams;
        },
        enumerable: true,
        configurable: true
    });

    const params: angular.IDirective = {
        controller: controller,
        scope: object.scope || false,
        replace: object.replace || false,
        transclude: object.transclude || false,
        require: object.require,
        compile: object.compile,
        link: object.link
    };

    if (object.link) {
        params.link = function () {
            return object.link.apply(this, arguments as any);
        };
    }

    if (object.compile) {
        params.compile = function () {
            return object.compile.apply(this, arguments as any);
        };
    }

    if (typeof object.template == 'string') {
        params.template = drMaxRepeatExpressionCompile(object.template);
    } else if (object.template) {
        params.template = function () {
            const objectTemplate = object?.template;
            const template = typeof objectTemplate === 'function' ? objectTemplate?.apply(this, arguments as any) : objectTemplate;
            return drMaxRepeatExpressionCompile(template);
        };
    }

    const name = object.selector.replace(/-([a-z])/g, matches => {
        return matches[1].toUpperCase();
    });

    adapter.linkDirectiveTMax(name, function () {
        return params;
    });
    return controller;
}

function decorateHard<Scope extends IScope, TElement extends JQLite>(object: IA9Component<Scope, TElement>, controller: angular.IController) {
    class ChildDirective extends Directive {
        public static object: IA9Component<Scope, TElement>;
        public static controller: angular.IController;

        public static get $name(): string {
            return ChildDirective.object.selector.replace(/-([a-z])/g, matches => {
                return matches[1].toUpperCase();
            });
        }

        public static get $scope(): IA9Component<Scope, TElement>['scope'] {
            return ChildDirective.object.scope || false;
        }

        public static get $restrict(): IA9Component<Scope, TElement>['restrict'] {
            return ChildDirective.object.restrict;
        }

        public static get $replace(): IA9Component<Scope, TElement>['replace'] {
            return ChildDirective.object.replace || false;
        }

        public static get $require(): IA9Component<Scope, TElement>['require'] {
            return ChildDirective.object.require;
        }

        public static get $controller(): angular.IController {
            return ChildDirective.controller;
        }

        public static get $inject(): string[] {
            return ChildDirective.object.providers || [];
        }

        public static get $bindToController(): IA9Component<Scope, TElement>['bindToController'] {
            return ChildDirective.object.bindToController || false;
        }

        public static get $controllerAs(): IA9Component<Scope, TElement>['controllerAs'] {
            return ChildDirective.object.controllerAs;
        }

        public static get $transclude(): IA9Component<Scope, TElement>['transclude'] {
            return ChildDirective.object.transclude || false;
        }

        public static get $terminal(): boolean {
            return ChildDirective.object.terminal || false;
        }

        public static get $priority(): number | null {
            return ChildDirective.object.priority || null;
        }

        public get noTemplate(): boolean {
            return !object.template;
        }

        public template(): string {
            const objectTemplate = object?.template;
            const template = typeof objectTemplate === 'function' ? objectTemplate?.apply(this, arguments as any) : objectTemplate;
            return drMaxRepeatExpressionCompile(template);
        }

        public link(): any {
            return object?.link?.apply(this, arguments as any);
        }

        public canCompile(): any {
            return object.compile;
        }

        public compile() {
            return object.compile && object.compile.apply(this, arguments as any);
        }

        constructor() {
            super();
            ChildDirective.$inject.forEach((injector: string, k: number) => {
                this[injector] = arguments[k];
            });
        }
        [injector: string]: any;
    }

    ChildDirective.object = object;
    ChildDirective.controller = controller;
    ChildDirective.controller.getDirective = function () {
        return ChildDirective;
    };
    adapter.linkDirective(ChildDirective);

    if (object.priority) {
        adapter.module.decorator(ChildDirective.$name + 'Directive', function Decorator($delegate) {
            const directive = $delegate.reduce(function (sel, item) {
                if (!sel || !sel.priority || (item.priority && item.priority < sel.priority)) {
                    sel = item;
                }
                return sel;
            }, null);
            return [directive];
        });
    }

    const injectorParams = A9DecoratorsCommon.parseConstructorParams(controller);

    Object.defineProperty(ChildDirective.controller, '$inject', {
        get: function () {
            return injectorParams;
        },
        enumerable: true,
        configurable: true
    });

    return ChildDirective.controller;
}

export function A9Component<Scope extends IScope, TElement extends JQLite = JQLite>(object: IA9Component<Scope, TElement>) {
    const liteProps = ['selector', 'scope', 'replace', 'transclude', 'require', 'compile', 'link', 'template'];
    let bLite = true;
    const props = Object.keys(object);
    for (let index = 0, length = props.length; index < length; index++) {
        if (liteProps.indexOf(props[index]) === -1) {
            bLite = false;
            break;
        }
    }
    if (bLite) {
        return function (controller: angular.IController) {
            return decorateLite(object, controller);
        };
    } else {
        return function (controller: angular.IController) {
            return decorateHard(object, controller);
        };
    }
}
